"use client";

import React, { useEffect, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import gsap from "gsap";

export default function TermsPage() {
  const pageRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // GSAP animations
  useEffect(() => {
    const ctx = gsap.context(() => {
      // Animate the page title
      gsap.fromTo(
        titleRef.current,
        { y: -20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, ease: "power3.out" }
      );

      // Animate the content with a slight delay
      gsap.fromTo(
        contentRef.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, delay: 0.3, ease: "power2.out" }
      );
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={pageRef} className="container mx-auto px-4 py-12 max-w-6xl">
      <h1
        ref={titleRef}
        className="text-3xl md:text-4xl font-bold mb-8 text-center"
      >
        Terms and Conditions of Hire – Plant and Equipment
      </h1>

      <div ref={contentRef}>
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold">R&R Equipment Rentals</h2>
              <p>546 Burnside Rd, Gilberton Qld 4208</p>
              <p>Phone: 0477 111 731</p>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="definitions" className="mb-12">
          <TabsList className="grid grid-cols-2 md:grid-cols-4 mb-8">
            <TabsTrigger value="definitions">
              Definitions & Acceptance
            </TabsTrigger>
            <TabsTrigger value="hire">Hire Terms</TabsTrigger>
            <TabsTrigger value="responsibilities">Responsibilities</TabsTrigger>
            <TabsTrigger value="legal">Legal & Other</TabsTrigger>
          </TabsList>

          <TabsContent value="definitions" className="space-y-6">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4">
                  1. DEFINITIONS AND INTERPRETATION
                </h3>
                <p className="mb-2">
                  (Definitions): Unless the context requires otherwise:
                </p>

                <ScrollArea className="h-[400px] rounded-md border p-4">
                  <div className="space-y-4">
                    <div>
                      <p className="font-medium">Commencement Date:</p>
                      <p>
                        The date and time noted in the Schedule when the hire of
                        equipment is to commence.
                      </p>
                    </div>

                    <div>
                      <p className="font-medium">Completion Date:</p>
                      <p>
                        The date and time noted in the Schedule, or as extended
                        under clause 2, when the hire is to cease.
                      </p>
                    </div>

                    <div>
                      <p className="font-medium">Confidential Information:</p>
                      <p>
                        Includes the Schedule, Hire Fee, payment information,
                        and details about the equipment that are not publicly
                        available.
                      </p>
                    </div>

                    <div>
                      <p className="font-medium">Damage Waiver Fee:</p>
                      <p>The amount noted in the Schedule.</p>
                    </div>

                    <div>
                      <p className="font-medium">Excess Amount:</p>
                      <p>The amount payable as per the Rates Schedule.</p>
                    </div>

                    <div>
                      <p className="font-medium">GST:</p>
                      <p>
                        As defined in the A New Tax System (Goods and Services
                        Tax) Act 1999 (Cth).
                      </p>
                    </div>

                    <div>
                      <p className="font-medium">Hire Fee:</p>
                      <p>The total price for hire as noted in the Schedule.</p>
                    </div>

                    <div>
                      <p className="font-medium">Hire Period:</p>
                      <p>The duration from Commencement to Completion Date.</p>
                    </div>

                    <div>
                      <p className="font-medium">Hire Rates:</p>
                      <p>Fees and charges listed in the Rates Schedule.</p>
                    </div>

                    <div>
                      <p className="font-medium">Hirer:</p>
                      <p>The individual or entity entering the agreement.</p>
                    </div>

                    <div>
                      <p className="font-medium">R&R Equipment Rentals:</p>
                      <p>
                        Refers to the business operating at 546 Burnside Rd,
                        Gilberton Qld 4208.
                      </p>
                    </div>

                    <div>
                      <p className="font-medium">Insolvency Event:</p>
                      <p>
                        Includes bankruptcy, liquidation, administration, and
                        similar financial distress.
                      </p>
                    </div>

                    <div>
                      <p className="font-medium">Plant and Equipment:</p>
                      <p>
                        Includes Water Trucks, Fuel Trailers, Water Trailers,
                        Car Trailers, Enclosed Trailers, Boat Trailers, Plant
                        Trailers, Generators, High-Pressure Jet Trailers, Wood
                        Chippers, and Box Trailers.
                      </p>
                    </div>

                    <div>
                      <p className="font-medium">Quote:</p>
                      <p>An estimate provided for hire purposes.</p>
                    </div>

                    <div>
                      <p className="font-medium">Rates Schedule:</p>
                      <p>Published pricing for services and equipment.</p>
                    </div>

                    <div>
                      <p className="font-medium">Schedule:</p>
                      <p>
                        The form completed by the Hirer offering to hire
                        equipment.
                      </p>
                    </div>

                    <div>
                      <p className="font-medium">Taxes:</p>
                      <p>
                        Includes all taxes, levies, duties, or other
                        government-imposed charges.
                      </p>
                    </div>

                    <div>
                      <p className="font-medium">Terms:</p>
                      <p>These conditions of hire.</p>
                    </div>

                    <div>
                      <p className="font-medium">(Interpretation rules):</p>
                      <p>
                        As per legal standards, including singular/plural usage,
                        gender inclusion, document referencing, and more.
                      </p>
                    </div>
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4">2. ACCEPTANCE</h3>
                <p>
                  The Hirer agrees to hire equipment from R&R Equipment Rentals
                  under these Terms and the Schedule. Acceptance begins on the
                  Commencement Date and ends at the earlier of Completion Date
                  or termination per clause 11.
                </p>
                <p className="mt-2">
                  Hirers must ensure all provided information is accurate. These
                  Terms prevail over any inconsistent terms provided by the
                  Hirer.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="hire" className="space-y-6">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4">
                  3. HIRE OF EQUIPMENT
                </h3>
                <p>
                  Hire begins on the Commencement Date and ends on the
                  Completion Date. Equipment must be returned to the designated
                  location or picked up as scheduled. Late returns may be
                  treated as theft.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4">
                  4. NO PPS LEASE WITHOUT OWNER AGREEMENT
                </h3>
                <p>
                  Hire periods cannot exceed two years without written consent,
                  in line with PPS Law.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4">
                  5. PAYMENT FOR HIRE
                </h3>
                <p>
                  Hire Fees and applicable charges must be paid per the
                  Schedule. Failure to pay may incur continued charges. Credit
                  card fees may apply.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4">
                  6. DAMAGE WAIVER FEE
                </h3>
                <p>
                  If selected and paid, this fee limits liability for accidental
                  damage—exceptions apply (e.g., misuse, theft, lack of
                  maintenance). It does not cover third-party damage or
                  breach-related incidents.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="responsibilities" className="space-y-6">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4">
                  7. USE, OPERATION, AND MAINTENANCE
                </h3>
                <p>
                  Equipment must only be used by trained individuals and as
                  intended. The Hirer must comply with safety laws and return
                  equipment clean and maintained.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4">
                  8. HIRER'S OBLIGATIONS
                </h3>
                <p>The Hirer agrees:</p>
                <ul className="list-disc pl-6 mt-2 space-y-2">
                  <li>To accurate Schedule details</li>
                  <li>Not to tamper with the equipment</li>

                  <li>Not to sub-hire without consent</li>
                  <li>Not to disable tracking systems</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4">
                  9. LOSS, DAMAGE OR BREAKDOWN
                </h3>
                <p>
                  Hirer is responsible for loss or damage, barring fair wear and
                  tear. All breakdowns must be immediately reported.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4">
                  10. EQUIPMENT RETURN AND FUEL REQUIREMENTS
                </h3>
                <p className="font-semibold">Fuel Return Policy:</p>
                <p>
                  All equipment must be returned with a full tank of fuel.
                  Failure to return equipment with a full tank will result in a
                  fuel charge of $3.00 per litre, which will be deducted from
                  your security deposit.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4">
                  11. MAINTENANCE AND DAMAGE CHARGES
                </h3>
                <p className="font-semibold">Clogging and Blockage:</p>
                <p>
                  If hired equipment becomes clogged due to overfeeding or
                  improper use, causing the machine to malfunction or stop
                  operating, and our team is required to attend and clear the
                  blockage, a maintenance charge of $500.00 will apply. This
                  charge will be deducted from your security deposit.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4">
                  12. SAFETY REQUIREMENTS
                </h3>
                <p className="font-semibold">Safety Manual:</p>
                <p>
                  Client must read and understand the safety manual provided
                  with the equipment hire before operation.
                </p>
                <p className="font-semibold mt-3">
                  Personal Protective Equipment (PPE):
                </p>
                <p>
                  All required PPE and protective gear must be worn during
                  equipment operation and must be supplied by the client.
                </p>
                <p className="font-semibold mt-3">Operating Guidelines:</p>
                <p>
                  Do not overfill or overfeed the chipper. Follow all
                  manufacturer guidelines and safety instructions to prevent
                  equipment damage and ensure safe operation.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4">
                  13. INDEMNITY AND LIABILITY
                </h3>
                <p>
                  The Hirer indemnifies R&R Equipment Rentals for damage,
                  injury, or loss arising from use, except where caused by the
                  company's negligence.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="legal" className="space-y-6">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4">14. TERMINATION</h3>
                <p>R&R may terminate the hire:</p>
                <ul className="list-disc pl-6 mt-2 space-y-2">
                  <li>With 2 days' notice</li>
                  <li>Immediately upon insolvency or breach</li>
                </ul>
                <p className="mt-2">
                  Hirer must return equipment and pay all outstanding fees.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <ScrollArea className="h-[400px] rounded-md border p-4">
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-2">
                        15. REPOSSESSION AND DEFAULT
                      </h3>
                      <p>
                        R&R may repossess equipment upon default. The Hirer
                        permits entry to recover equipment.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-2">
                        16. TITLE AND RISK
                      </h3>
                      <p>
                        R&R retains ownership. Risk transfers to the Hirer
                        during the Hire Period.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-2">
                        17. RATES SCHEDULE
                      </h3>
                      <p>
                        All hires are subject to the most current published
                        Rates Schedule unless otherwise agreed in writing.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-2">
                        18. PPS LAW
                      </h3>
                      <p>
                        The agreement may create a registrable security
                        interest. The Hirer must assist in registration and
                        waive rights to verification notices.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-2">
                        19. SECURITY INTERESTS AND SUB-HIRE
                      </h3>
                      <p>
                        Sub-hiring and third-party encumbrances require written
                        approval from R&R.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-2">
                        20. COMPETITION AND CONSUMER ACT 2010 (CCA)
                      </h3>
                      <p>
                        Nothing in these Terms limits rights under the CCA.
                        R&R's liability is limited where permitted.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-2">
                        21. PRIVACY ACT 1988
                      </h3>
                      <p>
                        R&R may collect, store, and disclose credit information
                        as required under law.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-2">
                        22. SECURITY AND CHARGE
                      </h3>
                      <p>
                        Hirer agrees to mortgage property to secure debts if
                        applicable. R&R may act as power of attorney.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-2">
                        23. CONFIDENTIALITY AND INTELLECTUAL PROPERTY
                      </h3>
                      <p>
                        Each party agrees to maintain confidentiality. No
                        intellectual property is transferred to the Hirer.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-2">
                        24. HIRER'S DISCLAIMER
                      </h3>
                      <p>
                        The Hirer disclaims any right to cancel based on
                        misrepresentation and accepts all risks.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-2">25. GST</h3>
                      <p>
                        GST is payable in addition to fees. Valid tax invoices
                        will be provided.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-2">
                        26. GENERAL
                      </h3>
                      <p>
                        These Terms are governed by Queensland law. Provisions
                        remain valid despite unenforceability of any part.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-2">
                        27. DISPUTE RESOLUTION
                      </h3>
                      <p>
                        Disputes (excluding payments) must be negotiated with
                        assistance from the Hire and Rental Industry Association
                        before litigation.
                      </p>
                    </div>
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="text-center">
          <Separator className="my-8" />
          <p className="text-sm text-gray-500">Last Updated: April 2025</p>
        </div>
      </div>
    </div>
  );
}
